// A small Markdown reader: CommonMark's common blocks and inlines plus GFM tables and
// strikethrough, to a typed tree. Raw HTML is never interpreted: it stays text, so whatever
// renders the tree (React, Typst) escapes it like any other text; a line that is only an HTML
// comment is dropped, as CommonMark renders it as nothing. Deliberately not a full
// CommonMark implementation: guides are written by people and agents in the everyday subset.

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: Inline[] }
  | { type: 'emph'; children: Inline[] }
  | { type: 'strike'; children: Inline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; title: string | null; children: Inline[] }
  | { type: 'image'; src: string; alt: string; title: string | null }
  | { type: 'break' };

export type Align = 'left' | 'center' | 'right' | null;

export type Block =
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: Inline[] }
  | { type: 'paragraph'; children: Inline[] }
  | { type: 'code'; lang: string | null; text: string }
  | { type: 'quote'; children: Block[] }
  | { type: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { type: 'table'; align: Align[]; header: Inline[][]; rows: Inline[][][] }
  | { type: 'rule' };

/** Longest source accepted (characters); longer input is cut, never parsed slowly. */
export const MAX_MARKDOWN_LENGTH = 1_000_000;
/** Deepest nesting of quotes and lists; deeper content is kept as paragraphs. */
export const MAX_DEPTH = 8;

const FENCE = /^( {0,3})(`{3,}|~{3,})\s*([^`\s]*)[^`]*$/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const BULLET = /^( {0,3})([-*+])([ \t]+|$)(.*)$/;
const ORDERED = /^( {0,3})(\d{1,9})([.)])([ \t]+|$)(.*)$/;
/** A line that is only an HTML comment: it renders nothing (guides keep tour directives in them). */
const COMMENT_LINE = /^ {0,3}<!--.*-->[ \t]*$/;
const TABLE_DELIM = /^ {0,3}\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const isBlank = (l: string) => /^\s*$/.test(l);

/** Parses Markdown into blocks. Never throws; unknown syntax is text. */
export function parseMarkdown(source: string): Block[] {
  const text = String(source ?? '')
    .slice(0, MAX_MARKDOWN_LENGTH)
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, '    ');
  return parseBlocks(text.split('\n'), 0);
}

function startsBlock(line: string): boolean {
  return (
    FENCE.test(line) ||
    COMMENT_LINE.test(line) ||
    HEADING.test(line) ||
    RULE.test(line) ||
    QUOTE.test(line) ||
    BULLET.test(line) ||
    ORDERED.test(line)
  );
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === '\\' && s[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (ch === '|') {
      cells.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

function parseBlocks(lines: string[], depth: number): Block[] {
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (isBlank(line) || COMMENT_LINE.test(line)) {
      i++;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const marker = fence[2]!;
      const indent = fence[1]!.length;
      const body: string[] = [];
      i++;
      while (i < lines.length) {
        const l = lines[i]!;
        const close = new RegExp(`^ {0,3}${marker[0] === '`' ? '`' : '~'}{${marker.length},}\\s*$`);
        if (close.test(l)) {
          i++;
          break;
        }
        body.push(l.slice(Math.min(indent, l.length - l.trimStart().length)));
        i++;
      }
      out.push({ type: 'code', lang: fence[3] ? fence[3] : null, text: body.join('\n') });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      out.push({
        type: 'heading',
        level: heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6,
        children: parseInlines(heading[2] ?? ''),
      });
      i++;
      continue;
    }

    if (RULE.test(line)) {
      out.push({ type: 'rule' });
      i++;
      continue;
    }

    if (QUOTE.test(line) && depth < MAX_DEPTH) {
      const inner: string[] = [];
      while (i < lines.length && !isBlank(lines[i]!)) {
        const q = QUOTE.exec(lines[i]!);
        if (q) inner.push(q[1]!);
        else if (startsBlock(lines[i]!)) break;
        else inner.push(lines[i]!); // lazy continuation
        i++;
      }
      out.push({ type: 'quote', children: parseBlocks(inner, depth + 1) });
      continue;
    }

    const bullet = BULLET.exec(line);
    const ordered = bullet ? null : ORDERED.exec(line);
    if ((bullet || ordered) && depth < MAX_DEPTH) {
      const isOrdered = !!ordered;
      const marker = bullet ? bullet[2]! : ordered![3]!;
      const start = ordered ? Number(ordered[2]) : 1;
      const items: Block[][] = [];
      while (i < lines.length) {
        const m = isOrdered ? ORDERED.exec(lines[i]!) : BULLET.exec(lines[i]!);
        if (!m) break;
        if ((isOrdered ? m[3] : m[2]) !== marker) break;
        const lead = m[1]!.length;
        const markerWidth = isOrdered ? m[2]!.length + 1 : 1;
        const gap = (isOrdered ? m[4] : m[3])!.length;
        const content = isOrdered ? m[5]! : m[4]!;
        const width = lead + markerWidth + Math.max(1, Math.min(gap, 4));
        const body: string[] = [content];
        i++;
        while (i < lines.length) {
          const l = lines[i]!;
          if (isBlank(l)) {
            // A blank line continues the item only when the next line is indented into it.
            const next = lines[i + 1];
            if (next !== undefined && !isBlank(next) && next.length - next.trimStart().length >= width) {
              body.push('');
              i++;
              continue;
            }
            break;
          }
          const ind = l.length - l.trimStart().length;
          if (ind >= width) body.push(l.slice(width));
          else if (!startsBlock(l))
            body.push(l.trimStart()); // lazy continuation of the paragraph
          else break;
          i++;
        }
        items.push(parseBlocks(body, depth + 1));
        // A blank line between items keeps the list going.
        if (i < lines.length && isBlank(lines[i]!)) {
          let j = i;
          while (j < lines.length && isBlank(lines[j]!)) j++;
          const again = j < lines.length && (isOrdered ? ORDERED : BULLET).exec(lines[j]!);
          if (again && (isOrdered ? again[3] : again[2]) === marker) i = j;
        }
      }
      out.push({ type: 'list', ordered: isOrdered, start, items });
      continue;
    }

    // A GFM table: a header row followed by a delimiter row.
    if (line.includes('|') && i + 1 < lines.length && TABLE_DELIM.test(lines[i + 1]!)) {
      const header = splitRow(line);
      const delims = splitRow(lines[i + 1]!);
      if (delims.length === header.length) {
        const align: Align[] = delims.map((d) => {
          const l = d.startsWith(':');
          const r = d.endsWith(':');
          return l && r ? 'center' : r ? 'right' : l ? 'left' : null;
        });
        i += 2;
        const rows: Inline[][][] = [];
        while (i < lines.length && !isBlank(lines[i]!) && lines[i]!.includes('|')) {
          const cells = splitRow(lines[i]!);
          rows.push(header.map((_, c) => parseInlines(cells[c] ?? '')));
          i++;
        }
        out.push({ type: 'table', align, header: header.map((h) => parseInlines(h)), rows });
        continue;
      }
    }

    // A paragraph: until a blank line or the start of another block.
    const para: string[] = [];
    while (i < lines.length && !isBlank(lines[i]!)) {
      if (para.length && startsBlock(lines[i]!)) break;
      para.push(lines[i]!);
      i++;
    }
    out.push({ type: 'paragraph', children: parseParagraph(para) });
  }
  return out;
}

function parseParagraph(lines: string[]): Inline[] {
  const out: Inline[] = [];
  lines.forEach((raw, idx) => {
    const last = idx === lines.length - 1;
    const hard = !last && (/ {2,}$/.test(raw) || /\\$/.test(raw));
    const line = raw.trim().replace(/\\$/, '');
    out.push(...parseInlines(line));
    if (!last) out.push(hard ? { type: 'break' } : { type: 'text', text: ' ' });
  });
  return mergeText(out);
}

function mergeText(nodes: Inline[]): Inline[] {
  const out: Inline[] = [];
  for (const n of nodes) {
    const prev = out[out.length - 1];
    if (n.type === 'text' && prev?.type === 'text') prev.text += n.text;
    else if (n.type !== 'text' || n.text) out.push(n);
  }
  return out;
}

const ESCAPABLE = /[\\`*_{}[\]()#+\-.!|~<>"']/;

/** Finds the `]` closing a bracket opened at `open` (nested brackets allowed), or -1. */
function closingBracket(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    const ch = s[i];
    if (ch === '\\') {
      i++;
      continue;
    }
    if (ch === '`') {
      const run = /^`+/.exec(s.slice(i))![0];
      const end = s.indexOf(run, i + run.length);
      if (end > 0) i = end + run.length - 1;
      continue;
    }
    if (ch === '[') depth++;
    else if (ch === ']') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** `(destination "title")` right after a bracket; the destination and title, and where it ends. */
function linkTarget(s: string, at: number): { href: string; title: string | null; end: number } | null {
  if (s[at] !== '(') return null;
  const m =
    /^\(\s*(<[^>\n]*>|[^\s()]*(?:\([^\s()]*\)[^\s()]*)*)(?:\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'))?\s*\)/.exec(
      s.slice(at),
    );
  if (!m) return null;
  let href = m[1]!;
  if (href.startsWith('<')) href = href.slice(1, -1);
  const title = m[2] ? m[2].slice(1, -1).replace(/\\(.)/g, '$1') : null;
  return { href: href.replace(/\\(.)/g, '$1'), title, end: at + m[0].length };
}

/** Parses one line (or cell) of inline Markdown. */
export function parseInlines(s: string, depth = 0): Inline[] {
  const out: Inline[] = [];
  let text = '';
  const flush = () => {
    if (text) out.push({ type: 'text', text });
    text = '';
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === '\\' && i + 1 < s.length && ESCAPABLE.test(s[i + 1]!)) {
      text += s[i + 1];
      i += 2;
      continue;
    }
    if (ch === '`') {
      const run = /^`+/.exec(s.slice(i))![0];
      const end = s.indexOf(run, i + run.length);
      if (end > 0) {
        flush();
        let code = s.slice(i + run.length, end);
        if (/^ .* $/.test(code) && code.trim()) code = code.slice(1, -1);
        out.push({ type: 'code', text: code });
        i = end + run.length;
        continue;
      }
      text += run;
      i += run.length;
      continue;
    }
    if (ch === '<') {
      const auto = /^<((?:https?:\/\/|mailto:)[^\s<>]+)>/i.exec(s.slice(i));
      if (auto) {
        flush();
        out.push({ type: 'link', href: auto[1]!, title: null, children: [{ type: 'text', text: auto[1]! }] });
        i += auto[0].length;
        continue;
      }
    }
    if ((ch === '!' && s[i + 1] === '[') || ch === '[') {
      const open = ch === '!' ? i + 1 : i;
      const close = closingBracket(s, open);
      const target = close > 0 ? linkTarget(s, close + 1) : null;
      if (target) {
        flush();
        const label = s.slice(open + 1, close);
        if (ch === '!')
          out.push({
            type: 'image',
            src: target.href,
            alt: plainText(parseInlines(label, depth + 1)),
            title: target.title,
          });
        else
          out.push({
            type: 'link',
            href: target.href,
            title: target.title,
            children: depth < MAX_DEPTH ? parseInlines(label, depth + 1) : [{ type: 'text', text: label }],
          });
        i = target.end;
        continue;
      }
    }
    if ((ch === '*' || ch === '_' || ch === '~') && depth < MAX_DEPTH) {
      const span = emphasis(s, i, depth);
      if (span) {
        flush();
        out.push(span.node);
        i = span.end;
        continue;
      }
    }
    text += ch;
    i++;
  }
  flush();
  return mergeText(out);
}

function emphasis(s: string, at: number, depth: number): { node: Inline; end: number } | null {
  const ch = s[at]!;
  const run = ch === '~' ? (s.startsWith('~~', at) ? '~~' : null) : s.startsWith(ch + ch, at) ? ch + ch : ch;
  if (!run) return null;
  const after = s[at + run.length];
  if (!after || /\s/.test(after)) return null;
  // `_` inside a word is not emphasis (snake_case stays as it is).
  if (ch === '_' && at > 0 && /[A-Za-z0-9]/.test(s[at - 1]!)) return null;
  let j = at + run.length;
  while (j < s.length) {
    const k = s.indexOf(run, j);
    if (k < 0) return null;
    const before = s[k - 1]!;
    const next = s[k + run.length];
    const closes =
      k > at + run.length &&
      !/\s/.test(before) &&
      before !== '\\' &&
      // a single `*` must not be the first half of a `**`
      (run.length === 2 || next !== ch) &&
      !(ch === '_' && next && /[A-Za-z0-9]/.test(next));
    if (closes) {
      const inner = parseInlines(s.slice(at + run.length, k), depth + 1);
      const node: Inline =
        ch === '~'
          ? { type: 'strike', children: inner }
          : run.length === 2
            ? { type: 'strong', children: inner }
            : { type: 'emph', children: inner };
      return { node, end: k + run.length };
    }
    j = k + run.length;
  }
  return null;
}

/** The text of inlines without markup (alt text, titles, the outline). */
export function plainText(nodes: readonly Inline[]): string {
  return nodes
    .map((n) => {
      switch (n.type) {
        case 'text':
        case 'code':
          return n.text;
        case 'image':
          return n.alt;
        case 'break':
          return ' ';
        default:
          return plainText(n.children);
      }
    })
    .join('');
}
